import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Archive, ArchiveRestore, Building2, Eye, Pencil, Plus, Search, ShieldAlert, Trash2, Upload } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const emptyForm = {
  company_name: "", vat_number: "", tax_code: "",
  first_name: "", last_name: "",
  certified_email: "", email: "",
  address: "", city: "", postal_code: "", sdi_code: "", phone: "",
  trade_name: "", contact_person: "", customer_code: "",
  province: "", country: "Italia", mobile: "",
  preferred_channel: "email", assigned_agent: "",
  payment_terms: "", internal_notes: "", notes: "",
};

const FILTERS = [
  { key: "active", label: "Attivi" },
  { key: "all", label: "Tutti" },
  { key: "archived", label: "Archiviati" },
  { key: "imported", label: "Importati" },
  { key: "with_cases", label: "Con pratiche aperte" },
  { key: "no_cases", label: "Senza pratiche" },
];

export default function Customers() {
  const { hasRole } = useAuth();
  const canEdit = hasRole("administrator", "reception", "customer_service");
  const canArchive = hasRole("administrator", "customer_service");
  const isAdmin = hasRole("administrator");

  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("active");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const PAGE_SIZE = 50;

  const [newDlg, setNewDlg] = useState(false);
  const [detail, setDetail] = useState(null); // {customer, cases, counts}
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [duplicateWarn, setDuplicateWarn] = useState(false);

  const [archiveDlg, setArchiveDlg] = useState(false);
  const [deleteDlg, setDeleteDlg] = useState(false);
  const [deletionImpact, setDeletionImpact] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [changeCodeDlg, setChangeCodeDlg] = useState(false);
  const [codeReason, setCodeReason] = useState("");
  const [newCode, setNewCode] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/customers`, { params: { filter, q: q || undefined, limit: PAGE_SIZE, skip: 0 } });
      setItems(r.data);
      setTotal(Number(r.headers["x-total-count"] || r.data.length));
      setHasMore((r.headers["x-has-more"] || "false") === "true");
    } catch (e) { toast.error("Errore caricamento clienti"); }
    finally { setLoading(false); }
  }, [filter, q]);

  const loadMore = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/customers`, { params: { filter, q: q || undefined, limit: PAGE_SIZE, skip: items.length } });
      setItems((prev) => [...prev, ...r.data]);
      setHasMore((r.headers["x-has-more"] || "false") === "true");
    } catch (e) { toast.error("Errore caricamento clienti"); }
    finally { setLoading(false); }
  }, [filter, q, items.length]);

  // Debounced server-side search: triggers on q change (min 0 chars) with 300ms debounce
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(t);
  }, [filter, q]);

  const openDetail = async (id) => {
    try {
      const r = await api.get(`/customers/${id}`);
      setDetail(r.data);
      setEditing(false);
      setForm({ ...emptyForm, ...r.data.customer });
      setDuplicateWarn(false);
    } catch (e) { toast.error("Errore apertura cliente"); }
  };

  const createCustomer = async () => {
    if (!form.company_name.trim()) return toast.error("Ragione sociale obbligatoria");
    try {
      const r = await api.post("/customers", form);
      toast.success("Cliente creato");
      if (r.data.duplicate_warning) toast.warning("Attenzione: potrebbe esistere un cliente simile.");
      setNewDlg(false); setForm(emptyForm); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const saveEdit = async () => {
    if (!detail) return;
    if (!form.company_name.trim()) return toast.error("Ragione sociale obbligatoria");
    try {
      const payload = { ...form };
      delete payload.customer_code;
      delete payload.id;
      delete payload.active;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.customer_code_history;
      delete payload.archived_at;
      delete payload.archived_by;
      const r = await api.patch(`/customers/${detail.customer.id}`, payload);
      setDetail({ ...detail, customer: r.data.customer });
      setDuplicateWarn(r.data.duplicate_warning);
      setEditing(false);
      toast.success("Anagrafica cliente aggiornata correttamente.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore aggiornamento"); }
  };

  const archive = async () => {
    if (!detail) return;
    try {
      await api.post(`/customers/${detail.customer.id}/archive`);
      toast.success("Cliente archiviato.");
      setArchiveDlg(false);
      openDetail(detail.customer.id); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const reactivate = async () => {
    if (!detail) return;
    try {
      await api.post(`/customers/${detail.customer.id}/reactivate`);
      toast.success("Cliente riattivato.");
      openDetail(detail.customer.id); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const openDeleteDialog = async () => {
    if (!detail) return;
    try {
      const r = await api.get(`/customers/${detail.customer.id}/deletion-impact`);
      setDeletionImpact(r.data);
      setDeleteConfirm(""); setAdminPassword("");
      setDeleteDlg(true);
    } catch (e) { toast.error("Errore controllo impatto"); }
  };

  const doDelete = async () => {
    if (!detail) return;
    try {
      await api.post(`/customers/${detail.customer.id}/delete-permanently`, { password: adminPassword, confirmation: deleteConfirm });
      toast.success("Cliente eliminato definitivamente.");
      setDeleteDlg(false); setDetail(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore eliminazione"); }
  };

  const doChangeCode = async () => {
    if (!detail) return;
    if (!newCode.trim() || !codeReason.trim()) return toast.error("Codice e motivazione sono obbligatori");
    try {
      await api.post(`/customers/${detail.customer.id}/change-code`, { new_code: newCode.trim(), reason: codeReason.trim() });
      toast.success("Codice cliente aggiornato.");
      setChangeCodeDlg(false); setNewCode(""); setCodeReason("");
      openDetail(detail.customer.id); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  // Server-side search + pagination — no client-side filtering.
  const filtered = items;

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto" data-testid="customers-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-[#0a0a2e]">Clienti installatori</h1>
          <p className="text-slate-500">Anagrafica clienti, storico assistenza, archiviazione ed eliminazione.</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="customers-search" className="pl-9 w-64" placeholder="Cerca…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          {canEdit && (
            <Button data-testid="customer-add-btn" onClick={() => { setForm(emptyForm); setNewDlg(true); }} className="ama-btn-primary rounded-full"><Plus className="w-4 h-4 mr-1" /> Nuovo</Button>
          )}
          {isAdmin && (
            <a href="/clienti/import" data-testid="btn-customer-import" className="inline-flex items-center gap-1 bg-white border border-slate-200 hover:border-[#d63384] rounded-full px-3 py-1.5 text-sm text-slate-700"><Upload className="w-4 h-4" /> Importa CSV</a>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="customer-filter-bar">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`customer-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${filter === f.key ? "bg-[#0a0a2e] text-white border-transparent" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`}
          >{f.label}</button>
        ))}
      </div>

      {loading && <div className="text-center text-slate-500 py-6">Caricamento…</div>}
      <div className="grid gap-2">
        {filtered.map((c) => {
          const isArchived = c.active === false;
          const isImported = !!c.import_batch_id;
          return (
            <div key={c.id} className={`ama-card p-4 flex items-center gap-3 ${isArchived ? "opacity-60" : ""}`} data-testid={`customer-row-${c.customer_code || c.id}`}>
              <div className="w-10 h-10 rounded-lg bg-fuchsia-50 flex items-center justify-center text-[#d63384]"><Building2 className="w-4 h-4" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-semibold text-[#0a0a2e]">{c.company_name}</div>
                  {isArchived && <Badge variant="outline" className="text-[10px]">Archiviato</Badge>}
                  {isImported && <Badge variant="outline" className="text-[10px]"><Upload className="w-3 h-3 mr-1" /> Importato</Badge>}
                  {c.customer_code && <Badge variant="secondary" className="text-[10px] font-mono">{c.customer_code}</Badge>}
                </div>
                <div className="text-xs text-slate-500 truncate">{c.contact_person} · {c.city} ({c.province}) · P.IVA {c.vat_number || "-"} · {c.email}</div>
              </div>
              <div className="flex gap-1">
                <Button data-testid={`customer-view-${c.customer_code || c.id}`} size="sm" variant="outline" onClick={() => openDetail(c.id)}><Eye className="w-4 h-4" /></Button>
                {canEdit && !isArchived && <Button data-testid={`customer-edit-${c.customer_code || c.id}`} size="sm" variant="outline" onClick={() => { openDetail(c.id).then(() => setEditing(true)); }}><Pencil className="w-4 h-4" /></Button>}
                {canArchive && !isArchived && <Button data-testid={`customer-archive-${c.customer_code || c.id}`} size="sm" variant="outline" onClick={() => { openDetail(c.id).then(() => setArchiveDlg(true)); }}><Archive className="w-4 h-4" /></Button>}
                {isAdmin && isArchived && <Button data-testid={`customer-reactivate-${c.customer_code || c.id}`} size="sm" variant="outline" onClick={() => { openDetail(c.id).then(reactivate); }}><ArchiveRestore className="w-4 h-4" /></Button>}
              </div>
            </div>
          );
        })}
        {!loading && filtered.length === 0 && <div className="ama-card p-8 text-center text-slate-500">Nessun cliente trovato.</div>}
        {hasMore && (
          <button
            type="button"
            data-testid="customers-load-more"
            onClick={loadMore}
            disabled={loading}
            className="mt-2 self-center rounded-full border border-slate-200 bg-white hover:border-[#d63384] px-4 py-2 text-sm font-semibold text-slate-700"
          >
            {loading ? "Caricamento…" : `Carica altri 50 clienti (${items.length} di ${total})`}
          </button>
        )}
        {!loading && !hasMore && items.length > 0 && (
          <div className="text-xs text-slate-400 text-center py-2">Fine risultati · {items.length} di {total}</div>
        )}
      </div>

      {/* New customer dialog */}
      <Dialog open={newDlg} onOpenChange={setNewDlg}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Nuovo cliente installatore</DialogTitle></DialogHeader>
          <CustomerFormFields form={form} setForm={setForm} allowEditCode />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewDlg(false)}>Annulla</Button>
            <Button data-testid="customer-save-btn" onClick={createCustomer} className="ama-btn-primary">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail / edit dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  {detail.customer.company_name}
                  {detail.customer.active === false && <Badge variant="outline" className="text-[10px]">Archiviato</Badge>}
                  {detail.customer.import_batch_id && <Badge variant="outline" className="text-[10px]">Importato</Badge>}
                </DialogTitle>
              </DialogHeader>

              {duplicateWarn && (
                <Alert variant="destructive" className="mb-2"><ShieldAlert className="w-4 h-4" /><AlertTitle>Possibile duplicato</AlertTitle><AlertDescription>Esiste già un cliente con questa Partita IVA.</AlertDescription></Alert>
              )}

              {!editing ? (
                <div className="space-y-4">
                  <div className="text-xs text-slate-500 border-b border-slate-100 pb-2">Codice cliente: <span className="font-mono font-bold text-[#0a0a2e]">{detail.customer.customer_code || "—"}</span></div>
                  <div className="grid grid-cols-2 gap-3 text-sm" data-testid="customer-detail-fields">
                    <KV k="Ragione sociale" v={detail.customer.company_name || "Non indicato"} />
                    <KV k="Partita IVA" v={detail.customer.vat_number || "Non indicato"} />
                    <KV k="Codice fiscale" v={detail.customer.tax_code || "Non indicato"} />
                    <KV k="Nome" v={detail.customer.first_name || "Non indicato"} />
                    <KV k="Cognome" v={detail.customer.last_name || "Non indicato"} />
                    <KV k="PEC" v={detail.customer.certified_email || "Non indicato"} />
                    <KV k="Email" v={detail.customer.email || "Non indicato"} />
                    <KV k="Via" v={detail.customer.address || "Non indicato"} />
                    <KV k="Città" v={detail.customer.city || "Non indicato"} />
                    <KV k="CAP" v={detail.customer.postal_code || "Non indicato"} />
                    <KV k="Codice SDI" v={detail.customer.sdi_code || "Non indicato"} />
                    <KV k="Telefono" v={detail.customer.phone || "Non indicato"} />
                  </div>
                  {(detail.customer.trade_name || detail.customer.province || detail.customer.mobile || detail.customer.assigned_agent) && (
                    <details className="text-sm">
                      <summary className="cursor-pointer text-xs uppercase tracking-wider text-slate-500 font-semibold">Altri dati</summary>
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        <KV k="Nome commerciale" v={detail.customer.trade_name} />
                        <KV k="Referente" v={detail.customer.contact_person} />
                        <KV k="Provincia" v={detail.customer.province} />
                        <KV k="Paese" v={detail.customer.country} />
                        <KV k="Cellulare" v={detail.customer.mobile} />
                        <KV k="Canale preferito" v={detail.customer.preferred_channel} />
                        <KV k="Agente" v={detail.customer.assigned_agent} />
                        <KV k="Condizioni pagamento" v={detail.customer.payment_terms} />
                      </div>
                    </details>
                  )}

                  <div>
                    <div className="text-xs uppercase font-semibold text-slate-500 mb-1">Pratiche collegate</div>
                    <div className="text-sm">{detail.counts?.repair_cases || 0} pratich{detail.counts?.repair_cases === 1 ? "a" : "e"} · {detail.counts?.repair_items || 0} prodott{detail.counts?.repair_items === 1 ? "o" : "i"}</div>
                  </div>

                  <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
                    {canEdit && detail.customer.active !== false && <Button data-testid="detail-edit-btn" variant="outline" onClick={() => setEditing(true)}><Pencil className="w-4 h-4 mr-1" /> Modifica cliente</Button>}
                    {isAdmin && detail.customer.active !== false && <Button data-testid="detail-change-code-btn" variant="outline" onClick={() => setChangeCodeDlg(true)}>Cambia codice cliente</Button>}
                    {canArchive && detail.customer.active !== false && <Button data-testid="detail-archive-btn" variant="outline" onClick={() => setArchiveDlg(true)}><Archive className="w-4 h-4 mr-1" /> Archivia cliente</Button>}
                    {isAdmin && detail.customer.active === false && <Button data-testid="detail-reactivate-btn" variant="outline" onClick={reactivate}><ArchiveRestore className="w-4 h-4 mr-1" /> Riattiva cliente</Button>}
                    {isAdmin && <Button data-testid="detail-delete-btn" variant="outline" className="text-rose-600 border-rose-300 hover:bg-rose-50" onClick={openDeleteDialog}><Trash2 className="w-4 h-4 mr-1" /> Elimina cliente</Button>}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <CustomerFormFields form={form} setForm={setForm} allowEditCode={false} />
                  <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                    <Button variant="outline" onClick={() => { setEditing(false); setForm({ ...emptyForm, ...detail.customer }); }}>Annulla</Button>
                    <Button data-testid="detail-save-btn" onClick={saveEdit} className="ama-btn-primary">Salva modifiche</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Archive confirm */}
      <Dialog open={archiveDlg} onOpenChange={setArchiveDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>Archiviare il cliente?</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-600">Vuoi archiviare questo cliente? Le pratiche e lo storico verranno conservati.</div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveDlg(false)}>Annulla</Button>
            <Button data-testid="archive-confirm-btn" onClick={archive} className="ama-btn-primary">Archivia cliente</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={deleteDlg} onOpenChange={setDeleteDlg}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="text-rose-600 flex items-center gap-2"><Trash2 className="w-5 h-5" /> Eliminazione definitiva</DialogTitle></DialogHeader>
          {deletionImpact && (
            !deletionImpact.can_delete ? (
              <Alert variant="destructive"><ShieldAlert className="w-4 h-4" /><AlertTitle>Impossibile eliminare</AlertTitle><AlertDescription>Il cliente non può essere eliminato perché possiede pratiche o documenti collegati. Può essere archiviato.<div className="mt-2 text-xs font-mono">{Object.entries(deletionImpact.related).map(([k, v]) => `${k}: ${v}`).join(" · ")}</div></AlertDescription></Alert>
            ) : (
              <div className="space-y-3">
                <Alert variant="destructive"><ShieldAlert className="w-4 h-4" /><AlertTitle>Questa operazione è irreversibile</AlertTitle><AlertDescription>{"Questa operazione eliminerà definitivamente l'anagrafica cliente e non potrà essere annullata."}</AlertDescription></Alert>
                <div>
                  <Label>Digita <b>ELIMINA CLIENTE</b> per confermare</Label>
                  <Input data-testid="delete-confirm-text" value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} placeholder="ELIMINA CLIENTE" />
                </div>
                <div>
                  <Label>Password amministratore</Label>
                  <Input data-testid="delete-admin-password" type="password" value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDeleteDlg(false)}>Annulla</Button>
                  <Button data-testid="delete-confirm-btn" onClick={doDelete} disabled={deleteConfirm !== "ELIMINA CLIENTE" || !adminPassword} className="bg-rose-600 hover:bg-rose-700 text-white">Elimina definitivamente</Button>
                </DialogFooter>
              </div>
            )
          )}
        </DialogContent>
      </Dialog>

      {/* Change customer code */}
      <Dialog open={changeCodeDlg} onOpenChange={setChangeCodeDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cambia codice cliente</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-600">{"Il codice cliente è normalmente immutabile. Il cambio verrà tracciato nell'audit."}</div>
            <div><Label>Codice attuale</Label><Input value={detail?.customer.customer_code || ""} disabled /></div>
            <div><Label>Nuovo codice *</Label><Input data-testid="change-code-input" value={newCode} onChange={(e) => setNewCode(e.target.value)} /></div>
            <div><Label>Motivo del cambio *</Label><Input data-testid="change-code-reason" value={codeReason} onChange={(e) => setCodeReason(e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeCodeDlg(false)}>Annulla</Button>
            <Button data-testid="change-code-confirm" onClick={doChangeCode} className="ama-btn-primary">Aggiorna codice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{k}</div>
      <div className="text-sm text-[#0a0a2e] font-medium">{v || "—"}</div>
    </div>
  );
}

function CustomerFormFields({ form, setForm, allowEditCode }) {
  const set = (k, v) => setForm({ ...form, [k]: v });
  const field = (k, label, { span = 1, required = false, type = "text" } = {}) => (
    <div className={span === 2 ? "sm:col-span-2" : ""}>
      <Label>{label}{required ? " *" : ""}</Label>
      <Input type={type} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`cust-field-${k}`} />
    </div>
  );
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {/* Exact required 12-field order */}
      {field("company_name", "Ragione sociale", { span: 2, required: true })}
      {field("vat_number", "Partita IVA")}
      {field("tax_code", "Codice fiscale")}
      {field("first_name", "Nome")}
      {field("last_name", "Cognome")}
      {field("certified_email", "PEC")}
      {field("email", "Email")}
      {field("address", "Via", { span: 2 })}
      {field("city", "Città")}
      {field("postal_code", "CAP")}
      {field("sdi_code", "Codice SDI")}
      {field("phone", "Telefono")}

      {/* Codice cliente sits ABOVE conceptually — shown as header/badge — kept read-only here */}
      {allowEditCode
        ? (<div className="sm:col-span-2 border-t border-slate-100 pt-2 mt-2"><Label>Codice cliente</Label><Input value={form.customer_code || ""} onChange={(e) => set("customer_code", e.target.value)} data-testid="cust-field-customer_code" /></div>)
        : (form.customer_code ? <div className="sm:col-span-2 text-xs text-slate-500 border-t border-slate-100 pt-2 mt-2">Codice cliente: <span className="font-mono font-bold text-[#0a0a2e]">{form.customer_code}</span></div> : null)}

      {/* Additional non-required fields — separate section */}
      <div className="sm:col-span-2 mt-2 pt-2 border-t border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold">Altri dati</div>
      {field("trade_name", "Nome commerciale")}
      {field("contact_person", "Referente")}
      {field("province", "Provincia")}
      {field("country", "Paese")}
      {field("mobile", "Cellulare")}
      <div>
        <Label>Canale preferito</Label>
        <select className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" value={form.preferred_channel || "email"} onChange={(e) => set("preferred_channel", e.target.value)}>
          <option value="email">Email</option><option value="pec">PEC</option>
          <option value="phone">Telefono</option><option value="whatsapp">WhatsApp</option>
        </select>
      </div>
      {field("assigned_agent", "Agente / Referente interno")}
      {field("payment_terms", "Condizioni di pagamento", { span: 2 })}
      {field("internal_notes", "Note interne", { span: 2 })}
    </div>
  );
}
